import classNames from "classnames";
import React, { useMemo, useRef, useState } from "react";
import { useDebouncedValue } from "../../../shared/hooks/useDebouncedValue";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntityPicker, { KuikaPickerChip, KuikaPickerFolder } from "../kuika-entity-picker/kuika-entity-picker";
import { KuikaListTableRowAction } from "../kuika-list-table/kuika-list-table";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import KuikaPopover from "../kuika-popover/kuika-popover";
import {
  KuikaPopupContainerContext,
  KuikaPopupContainerGetter
} from "../kuika-popup-container/kuika-popup-container-context";
import KuikaSvg, { KuikaSvgProps } from "../kuika-svg/kuika-svg";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-entity-switcher.scss";

/**
 * The chevron beside an editor's title that opens "which one am I editing, and which
 * others are there" — the navigation a tool's left panel used to provide before its list
 * moved to a landing page.
 *
 * Three tools converged on the same control (Process Automation's process switcher, the
 * Email Builder's template switcher, and now Scheduled Jobs), which is why it lives here
 * rather than being copied a third time. A caller owns only what is genuinely its own:
 * the entity's NAME, its glyph, and the ids its E2E suite targets.
 *
 * Selecting does not switch on its own — `onSelect` is expected to route through the
 * host's unsaved-changes handshake, exactly as the panels it replaced did.
 *
 * The panel it opens is `KuikaEntityPicker` — the UI Design screen selector's panel — so every
 * switcher in the designer reads as that one: folders where the kind has them, chips where it has
 * natural groups, a "…" menu with the actions the tool's own list offers, and a footer leading
 * back to that list.
 */

/** A group a chip filters to: "Active", "AI Agents", "Database". */
export interface KuikaEntitySwitcherGroup {
  key: string;
  label: string;
}

export type KuikaEntitySwitcherFolder = KuikaPickerFolder;

export interface KuikaEntitySwitcherItem {
  id: string;
  name: string;
}

export interface KuikaEntitySwitcherProps<T extends KuikaEntitySwitcherItem> {
  items: T[];
  current?: T;
  /**
   * The second argument is where the trigger sits when the pick was made. A host whose
   * unsaved-changes guard opens AT THE CLICK (the table row browser's) has no event of its
   * own to anchor on, and the switcher's own trigger is the only honest anchor.
   */
  onSelect: (item: T, triggerRect: DOMRect) => void;
  /** Omit to hide the "+" in the panel head — not every tool creates from here. */
  onCreateNew?: () => void;
  /** Singular, title case: "Email Template", "Process", "Scheduled Job". */
  entityLabel: string;
  /** Plural for the list heading and the search placeholder. Defaults to `entityLabel` + "s". */
  entityLabelPlural?: string;
  iconName: string;
  iconType?: KuikaSvgProps["type"];
  /** A glyph per record, for a list that mixes kinds (the AI functions). Defaults to `iconName`. */
  getItemIcon?: (item: T) => React.ReactNode;
  /** The kind's folders. Omit where it has none — the list then has no chevron column. */
  folders?: KuikaEntitySwitcherFolder[];
  /** Natural groups, drawn as filter chips with counts. Omit where the kind has none. */
  groups?: KuikaEntitySwitcherGroup[];
  getItemGroup?: (item: T) => string;
  /** The "…" menu on a record: the actions the tool's own list offers for it. */
  getRowActions?: (item: T) => KuikaDropdownMenuItemType[];
  /**
   * The same menu, as the tool's list declares it — pass the list's own action array and the
   * two menus cannot drift. Used when `getRowActions` is not.
   */
  rowActions?: KuikaListTableRowAction<T>[];
  getFolderActions?: (folder: KuikaEntitySwitcherFolder) => KuikaDropdownMenuItemType[];
  /** The "+" beside the search as a menu ("New X / New folder") rather than one action. */
  createMenu?: KuikaDropdownMenuItemType[];
  /** The footer's link back to the tool's list. */
  onManageAll?: () => void;
  panelTestId?: string;
  searchTestId?: string;
  createTestId?: string;
  /**
   * `chevron` (the default) draws only the caret, for a header that owns its own title
   * beside it. `chip` draws the identity itself — glyph, name and caret in one control —
   * which is what an editor's top bar wears: the name IS the way to switch it.
   */
  trigger?: "chevron" | "chip";
  /** Chip only: the glyph in front of the name. A bare 16px `KuikaSvg`, not a tile. */
  triggerIcon?: React.ReactNode;
  /**
   * Chip only: what the glyph IS, said on hover — "PostgreSQL", "SQL Action · OrdersDb". A tinted
   * silhouette shows the family, not the kind, so the words are the glyph's own. Hovering the
   * glyph shows it; clicking it still opens the switcher, like the rest of the chip.
   */
  triggerIconTooltip?: React.ReactNode;
  /** Chip only: the open record's name. */
  triggerLabel?: string;
  triggerLabelTestId?: string;
  /**
   * Chip only: decorate the name — the config editor wraps it in a tooltip. The chip still
   * owns the glyph, the caret and the geometry.
   */
  renderTriggerLabel?: (label: string) => React.ReactNode;
  triggerTestId?: string;
  /** Per-item test id; the switcher emits none by default. */
  itemTestId?: (item: T) => string;
  className?: string;
}

const ALL_GROUP: string = "__all__";

const POPOVER_CLASS: string = "kuika-entity-switcher__popover";

/**
 * Where the panel's own menus (a row's "…", the "+" choices) mount: inside the popover. The
 * popover is its own stacking context above the page, so a menu portaled to <body> would open
 * underneath it; inside, it paints over the panel and a click on it is not "outside".
 */
const popoverContainer: KuikaPopupContainerGetter = (triggerNode?: HTMLElement): HTMLElement =>
  triggerNode?.closest<HTMLElement>(`.${POPOVER_CLASS}`) ?? document.body;

/**
 * Case- and accent-blind, so "sirket" finds "Şirket". Built on `localeCompare`-free folding:
 * NFD strips the combining marks, and the dotless ı is mapped by hand because NFD leaves it be.
 */
const normalizeForMatch = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .toLowerCase();

const KuikaEntitySwitcher = <T extends KuikaEntitySwitcherItem>({
  items,
  current,
  onSelect,
  onCreateNew,
  entityLabel,
  entityLabelPlural,
  iconName,
  iconType = "common",
  getItemIcon,
  folders,
  groups,
  getItemGroup,
  getRowActions,
  rowActions,
  getFolderActions,
  createMenu,
  onManageAll,
  panelTestId,
  searchTestId,
  createTestId,
  trigger = "chevron",
  triggerIcon,
  triggerIconTooltip,
  triggerLabel,
  triggerLabelTestId,
  renderTriggerLabel,
  triggerTestId,
  itemTestId,
  className
}: KuikaEntitySwitcherProps<T>) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");
  const [activeGroup, setActiveGroup] = useState<string>(ALL_GROUP);
  const triggerRef: React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);

  const isChip: boolean = trigger === "chip";
  const label: string = triggerLabel ?? current?.name ?? "";

  const plural: string = entityLabelPlural ?? `${entityLabel}s`;

  /**
   * Sentence-cased copy lowercases the entity's name — except when the name carries an
   * ACRONYM, where "search apis" and "switch api" read as typos. Two adjacent capitals is
   * the test, so "API" and "APIs" survive while "Scheduled Job" still lowercases.
   */
  const spoken = (label: string): string => (/[A-Z]{2}/.test(label) ? label : label.toLowerCase());

  // The input echoes `query` per keystroke; the list re-filters only once typing pauses.
  const filterQuery: string = useDebouncedValue(query);

  const needle: string = normalizeForMatch(filterQuery.trim());

  /**
   * A folder whose NAME matches brings its records with it — the search says "and folders", and
   * finding a folder you cannot open would be a dead end.
   */
  const folderMatchedIds: Set<string> = useMemo((): Set<string> => {
    const ids: Set<string> = new Set<string>();
    if (!needle) return ids;
    const walk = (list: KuikaEntitySwitcherFolder[], inherited: boolean): void => {
      list.forEach((folder: KuikaEntitySwitcherFolder): void => {
        const hit: boolean = inherited || normalizeForMatch(folder.name).includes(needle);
        if (hit) folder.itemIds.forEach((id: string): void => void ids.add(id));
        walk(folder.children ?? [], hit);
      });
    };
    walk(folders ?? [], false);
    return ids;
  }, [folders, needle]);

  const matches: T[] = useMemo(
    (): T[] =>
      items.filter(
        (item: T): boolean =>
          (activeGroup === ALL_GROUP || !getItemGroup || getItemGroup(item) === activeGroup) &&
          (!needle || normalizeForMatch(item.name || "").includes(needle) || folderMatchedIds.has(item.id))
      ),
    [items, needle, folderMatchedIds, activeGroup, getItemGroup]
  );

  const chips: KuikaPickerChip[] | undefined =
    groups && getItemGroup
      ? [
          { key: ALL_GROUP, label: "All", count: items.length },
          ...groups.map((group: KuikaEntitySwitcherGroup): KuikaPickerChip => ({
            key: group.key,
            label: group.label,
            count: items.filter((item: T): boolean => getItemGroup(item) === group.key).length
          }))
        ]
      : undefined;

  const countFolders = (list: KuikaEntitySwitcherFolder[]): number =>
    list.reduce(
      (sum: number, folder: KuikaEntitySwitcherFolder): number => sum + 1 + countFolders(folder.children ?? []),
      0
    );

  const close = (): void => {
    setVisible(false);
    setQuery("");
    setActiveGroup(ALL_GROUP);
  };

  const handleSelect = (item: T, event: React.SyntheticEvent<HTMLElement>): void => {
    // Read the anchor BEFORE closing: the popover unmounts the trigger's open state and a
    // host that measures afterwards would get a stale box.
    const triggerRect: DOMRect =
      triggerRef.current?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect();
    close();
    if (item.id === current?.id) return;
    onSelect(item, triggerRect);
  };

  const handleCreateNew = (): void => {
    close();
    onCreateNew?.();
  };

  /** Every menu action closes the panel first: most of them navigate or open a dialog. */
  const closingOptions = (options: KuikaDropdownMenuItemType[]): KuikaDropdownMenuItemType[] =>
    options.map((option: KuikaDropdownMenuItemType): KuikaDropdownMenuItemType => ({
      ...option,
      onClick: (event: unknown): void => {
        close();
        option.onClick?.(event);
      }
    }));

  const resolveRowActions = (item: T): KuikaDropdownMenuItemType[] => {
    if (getRowActions) return getRowActions(item);
    return (rowActions ?? [])
      .filter((action: KuikaListTableRowAction<T>): boolean => !action.hidden?.(item))
      .map((action: KuikaListTableRowAction<T>): KuikaDropdownMenuItemType => ({
        key: action.key,
        label: action.label,
        icon: action.icon,
        danger: action.danger,
        dataTestId: action.dataTestId,
        onClick: (): void => action.onSelect(item)
      }));
  };

  const renderCreateControl = (): React.ReactNode => {
    const button = (onClick?: () => void): React.ReactNode => (
      <button
        type="button"
        className="kuika-picker__add"
        aria-label={`Create a new ${spoken(entityLabel)}`}
        data-testid={createTestId}
        onClick={onClick}
      >
        <KuikaSvg name="plus" width={16} height={16} />
      </button>
    );
    if (createMenu?.length) {
      return (
        <KuikaDropdownMenu options={closingOptions(createMenu)} placement="bottomRight">
          {button()}
        </KuikaDropdownMenu>
      );
    }
    return onCreateNew ? button(handleCreateNew) : null;
  };

  const renderMenu =
    <R,>(build: (row: R) => KuikaDropdownMenuItemType[]) =>
    (row: R, onOpenChange: (open: boolean) => void): React.ReactNode => {
      const options: KuikaDropdownMenuItemType[] = build(row).filter(
        (option: KuikaDropdownMenuItemType): boolean => !option.hidden
      );
      if (options.length === 0) return null;
      return (
        <KuikaDropdownMenu
          options={closingOptions(options)}
          placement="bottomRight"
          setOpen={onOpenChange}
          // The list scrolls under this popup, so it follows the row out of view by closing.
          closeOnScroll
        >
          <button type="button" className="kuika-picker__row-menu" aria-label="More actions">
            <KuikaSvg name="dots" width={16} height={16} />
          </button>
        </KuikaDropdownMenu>
      );
    };

  const folderTotal: number | undefined = folders ? countFolders(folders) : undefined;

  const renderTriggerIcon = (): React.ReactNode => {
    if (!triggerIcon || !triggerIconTooltip) return triggerIcon;
    return (
      <KuikaTooltip content={triggerIconTooltip} position="bottom">
        {/* A real element for the tooltip to hang its hover on: `KuikaSvg` takes no ref. Nothing
            stops the click here, so it bubbles to the chip and opens the switcher as before. */}
        <span className="kuika-entity-switcher__trigger-icon">{triggerIcon}</span>
      </KuikaTooltip>
    );
  };

  return (
    <KuikaPopover
      visible={visible}
      onVisibleChange={(next: boolean): void => (next ? setVisible(true) : close())}
      align="start"
      hideCloseIcon
      className={POPOVER_CLASS}
      content={
        <KuikaPopupContainerContext.Provider value={popoverContainer}>
          <KuikaEntityPicker<T>
            panelTestId={panelTestId}
            items={matches}
            folders={folders}
            currentId={current?.id}
            isFiltering={!!needle || activeGroup !== ALL_GROUP}
            query={query}
            onQueryChange={setQuery}
            searchPlaceholder={`Search ${spoken(plural)}${folders ? " and folders" : ""}...`}
            searchTestId={searchTestId}
            createControl={renderCreateControl()}
            chips={chips}
            activeChip={activeGroup}
            onChipChange={setActiveGroup}
            renderItemIcon={(item: T): React.ReactNode =>
              getItemIcon ? getItemIcon(item) : <KuikaSvg name={iconName} type={iconType} width={16} height={16} />
            }
            onSelect={handleSelect}
            renderItemMenu={getRowActions || rowActions ? renderMenu(resolveRowActions) : undefined}
            renderFolderMenu={
              getFolderActions ? renderMenu((folder: KuikaEntitySwitcherFolder) => getFolderActions(folder)) : undefined
            }
            itemTestId={itemTestId}
            emptyText={`No ${spoken(entityLabel)} found`}
            footerCount={`${items.length} ${spoken(items.length === 1 ? entityLabel : plural)}${
              folderTotal === undefined ? "" : ` · ${folderTotal} ${folderTotal === 1 ? "folder" : "folders"}`
            }`}
            manageLabel={`Manage all ${spoken(plural)}`}
            onManage={
              onManageAll
                ? (): void => {
                    close();
                    onManageAll();
                  }
                : undefined
            }
          />
        </KuikaPopupContainerContext.Provider>
      }
    >
      <button
        ref={triggerRef}
        type="button"
        className={classNames("kuika-entity-switcher__trigger", className, {
          "kuika-entity-switcher__trigger--open": visible,
          // The whole chip is the reveal host, so the name slides while anywhere on the
          // control is hovered — the caret included.
          [`kuika-entity-switcher__trigger--chip ${OVERFLOW_REVEAL_HOST_CLASS}`]: isChip
        })}
        data-testid={triggerTestId}
        aria-label={`Switch ${spoken(entityLabel)}`}
      >
        {isChip && renderTriggerIcon()}
        {isChip && (
          <KuikaTextMedium className="kuika-entity-switcher__trigger-name" data-testid={triggerLabelTestId}>
            {/* The name is the one thing a caller cannot shorten, and the bar is a fixed
                band, so a long one is read by hovering it rather than cut with an ellipsis. */}
            <KuikaOverflowText>{renderTriggerLabel ? renderTriggerLabel(label) : label}</KuikaOverflowText>
          </KuikaTextMedium>
        )}
        <KuikaSvg name={visible ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
      </button>
    </KuikaPopover>
  );
};

export default KuikaEntitySwitcher;
